import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CardContainerMC from '../../components/CardContainerMC';
import giftIcon from '../../../../assets/images/gift.png';

const WsTemplatePreviewMC = ({
  type,
  headerText,
  templateText,
  templateValues,
  quickReplies,
  callToActions,
  file,
  authButton = { text: '' },
  expirationOffer,
  headerOfferTitle,
  offerButtonText,
  offerButtonValue,
  offerUrlButtonText,
  expirationDate,
  example,
  showOnlyChatPreview = false,
  cards
}) => {
  const { t } = useTranslation('WsTemplatesMC');
  const { t: tGlobal } = useTranslation();
  const copyCodeButtonText = tGlobal('ws_templates.defaults.copy_code_button');

  const textToShow = useMemo(() => {
    if (showOnlyChatPreview) {
      return templateText;
    }

    const variables = templateText.match(/\{\{\d+\}\}/g);
    let text = templateText;
    if (variables) {
      variables.forEach((item, idx) => {
        if (templateValues[idx]) {
          text = text.replace(item, templateValues[idx]);
        }
      });
    }
    return text;
  }, [templateValues, templateText, showOnlyChatPreview]);

  const displayTextForChat = showOnlyChatPreview ? textToShow : (example || textToShow);
  const displayTextForPreview = textToShow;

  const completePreviewText = useMemo(() => {
    let preview = '';

    // Add header offer title if exists
    if (headerOfferTitle) {
      preview += `${headerOfferTitle}\n`;
      if (expirationOffer && expirationDate) {
        preview += `${t('modal.expiry_date', { 
          expirationDate: expirationDate.toLocaleString()
        })}\n`;
      }
      if (offerButtonValue) {
        preview += `${offerButtonValue}\n\n`;
      }
    }

    // Add main text (always use casted text for preview)
    preview += displayTextForPreview;

    // Add buttons in original format
    const hasButtons =
      callToActions.length > 0 ||
      quickReplies.length > 0 ||
      authButton.text ||
      offerUrlButtonText ||
      offerButtonText;
    if (hasButtons) {
      preview += ' ';

      // Add call to action buttons
      callToActions.forEach((cta) => {
        if (cta.text) {
          preview += `| [${cta.text}${cta.url ? `, ${cta.url}` : ''}] `;
        }
      });

      // Add quick replies
      quickReplies.forEach((qr) => {
        if (qr.text) {
          preview += `| [${qr.text}] `;
        }
      });

      // Add auth button
      if (authButton.text) {
        preview += `| [${copyCodeButtonText}] `;
      }

      // Add offer buttons
      if (offerUrlButtonText) {
        preview += `| [${offerUrlButtonText}] `;
      }

      if (offerButtonText) {
        preview += `| [${offerButtonText}] `;
      }
    }

    return preview.trim();
  }, [
    headerText,
    headerOfferTitle,
    expirationOffer,
    expirationDate,
    offerButtonValue,
    displayTextForPreview,
    callToActions,
    quickReplies,
    authButton,
    offerUrlButtonText,
    offerButtonText,
    copyCodeButtonText
  ]);

  const getFilePlaceholder = () => {
    switch (type) {
      case 'image':
        return 'mi-image-outline';
      case 'video':
        return 'mi-video-line';
      case 'document':
        return 'mi-document-outline';
      default:
        return '';
    }
  };

  return (
    <div className="tw-h-full tw-flex tw-flex-col">
      {!showOnlyChatPreview && (
        <>
          <div className="tw-pb-3">
            <p className="tw-text-sm tw-font-semibold tw-mb-4">{t('modal.preview')}</p>
            <p className="tw-text-gray-700 tw-whitespace-pre-line tw-leading-relaxed">
              {completePreviewText}
            </p>
          </div>

          <div className="tw-pb-3 tw-mt-4">
            <p className="tw-text-sm tw-font-semibold tw-mb-0">{t('modal.example')}</p>
          </div>
        </>
      )}

      <div
        className="tw-flex-1 tw-pb-4 tw-border tw-border-gray-200 tw-rounded-lg"
        style={{ backgroundColor: '#F9F7F5' }}
      >
        <div className="tw-bg-white tw-rounded-t-lg tw-px-4 tw-py-3 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div>
              <div className="tw-font-semibold">{t('modal.chat')}</div>
              <div className="tw-text-sm tw-text-gray-700">{t('modal.online')}</div>
            </div>
          </div>
        </div>

        <CardContainerMC className="tw-mt-0 tw-max-w-md tw-mx-auto tw-bg-transparent tw-shadow-none tw-p-0">
         <div className="tw-w-full">
            {(!!templateText || headerText || file || headerOfferTitle || expirationOffer) && (
              <div className="tw-space-y-3 tw-bg-white tw-p-3 tw-rounded-lg tw-shadow-sm">
                {!!headerText && (
                  <div className="tw-font-semibold tw-text-gray-800 tw-text-sm tw-mb-2">
                    {headerText}
                  </div>
                )}

                {type !== 'text' && type !== 'carousel' && !file && (
                  <div className="tw-bg-gray-100 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-py-16 tw-mb-3">
                    <i className={`${getFilePlaceholder()} tw-text-gray-400 tw-text-6xl`} />
                  </div>
                )}

                {type === 'image' && file && (
                  <div className="tw-rounded-lg tw-mb-3 tw-h-48 tw-overflow-hidden">
                    <img
                      src={URL.createObjectURL(file)}
                      className="tw-w-full tw-h-full tw-object-cover"
                      alt="preview"
                    />
                  </div>
                )}

                {type === 'video' && file && (
                  <div className="tw-rounded-lg tw-mb-3 tw-overflow-hidden">
                    <video
                      src={URL.createObjectURL(file)}
                      className="tw-w-full tw-max-h-48"
                      controls
                    />
                  </div>
                )}

                {type === 'document' && file && (
                  <div className="tw-bg-gray-100 tw-rounded-lg tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-mb-3 tw-relative">
                    <i className="mi-document-outline tw-text-gray-400 tw-text-6xl tw-mb-4" />
                    <div className="tw-flex tw-items-center tw-text-xs tw-text-gray-600">
                      <i className="mi-pdf-fill tw-text-lg tw-mr-2" />
                      <span className="tw-truncate tw-max-w-[150px]">{file.name}</span>
                    </div>
                  </div>
                )}

                {headerOfferTitle && (
                  <div className="tw-bg-green-50 tw-rounded-lg tw-p-3 tw-mb-3 tw-border tw-border-green-200">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-w-8 tw-h-8 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
                        <img src={giftIcon} alt="Gift" className="tw-w-5 tw-h-5" />
                      </div>
                      <div className="tw-flex-1">
                        <div className="tw-font-semibold tw-text-gray-800 tw-text-sm">
                          {headerOfferTitle}
                        </div>
                        {expirationOffer && expirationDate && (
                          <div className="tw-text-xs tw-text-gray-600">
                            {t('modal.expiry_date', { 
                              expirationDate: expirationDate.toLocaleString()
                            })}
                          </div>
                        )}
                        <div className="tw-font-semibold tw-text-gray-500 tw-text-xs">
                          {offerButtonValue}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="tw-text-gray-800 tw-whitespace-pre-line tw-leading-relaxed">
                  {displayTextForChat}
                </div>

                <div className="tw-text-xs tw-text-gray-500 tw-text-right tw-mt-3 tw-flex tw-items-center tw-justify-end tw-gap-1">
                  <span>
                    {new Date().toLocaleDateString('es-ES', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric' 
                    }).replace(/\//g, '-')} {new Date().toLocaleTimeString('es-ES', { 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      hour12: false 
                    })}
                  </span>
                  <i className="mi-check-double-line tw-text-blue-500" />
                </div>

                {(callToActions.length > 0 ||
                  quickReplies.length > 0 ||
                  authButton.text ||
                  offerUrlButtonText ||
                  offerButtonText) && (
                  <div className="tw-space-y-0 tw-mt-4">
                    {callToActions.map((cta, index) => (
                      <div key={cta.index || index}>
                        <hr className="tw-border-gray-200" />
                        <div className="tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-blue-600 tw-text-sm tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                          <i
                            className={`${
                              cta.type === 'PHONE_NUMBER' ? 'mi-telephone-fill' : 'mi-url-share'
                            } tw-text-blue-500`}
                          />
                          <span>{cta.text || ''}</span>
                        </div>
                      </div>
                    ))}

                    {quickReplies.map((qr, index) => (
                      <div key={qr.index || index}>
                        <hr className="tw-border-gray-200" />
                        <div className="tw-py-3 tw-flex tw-items-center tw-justify-center tw-text-blue-600 tw-text-sm tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors tw-min-h-[37px]">
                          <span>{qr.text || ''}</span>
                        </div>
                      </div>
                    ))}

                    {!!authButton.text && (
                      <div>
                        <hr className="tw-border-gray-200" />
                        <div className="tw-py-3 tw-flex tw-items-center tw-justify-center tw-text-blue-600 tw-text-sm tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                          <span>{copyCodeButtonText}</span>
                        </div>
                      </div>
                    )}

                    {offerUrlButtonText && (
                      <div>
                        <hr className="tw-border-gray-200" />
                        <div className="tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-blue-600 tw-text-sm tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                          <i className="mi-url-share tw-text-blue-500" />
                          <span>{offerUrlButtonText}</span>
                        </div>
                      </div>
                    )}

                    {offerButtonText && (
                      <div>
                        <hr className="tw-border-gray-200" />
                        <div className="tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-text-blue-600 tw-text-sm tw-cursor-pointer hover:tw-bg-blue-50 tw-transition-colors">
                          <i className="mi-copy-outline tw-text-blue-500" />
                          <span>{offerButtonText}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {type === 'carousel' && cards && cards.length > 0 && (
              <div className="tw-flex tw-flex-row tw-overflow-x-auto tw-gap-3 tw-pb-2 tw-mt-2 tw-snap-x">
                {cards.map((card, index) => (
                  <div key={index} className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-min-w-[200px] tw-w-[200px] tw-snap-start tw-border tw-border-gray-200 tw-overflow-hidden">
                    <div className="tw-h-[120px] tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border-b tw-border-gray-100">
                      {card.headerType === 'VIDEO' ? (
                          <i className="mi-video-line tw-text-gray-400 tw-text-4xl" />
                      ) : (
                          <i className="mi-image-outline tw-text-gray-400 tw-text-4xl" />
                      )}
                    </div>
                    <div className="tw-p-3">
                      <div className="tw-text-gray-800 tw-text-sm tw-whitespace-pre-line tw-mb-3">
                        {card.body || card.description}
                      </div>
                      
                      {card.buttons && card.buttons.map((btn, btnIndex) => (
                        <div key={btnIndex} className="tw-mt-2">
                           <div className="tw-w-full tw-py-2 tw-px-3 tw-bg-gray-50 tw-text-blue-600 tw-text-xs tw-font-medium tw-rounded tw-text-center tw-truncate tw-border tw-border-gray-100">
                             {btn.text}
                           </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContainerMC>
      </div>
    </div>
  );
};

export default WsTemplatePreviewMC;
